import createAppSlice from 'state/createAppSlice'
import { StepsState } from 'types/shared'
import { getInteractionModel } from '../actions'

export const REVIEW_STEP_UUID = 'review'

export const stepsInitialState: StepsState = {
  allSteps: [],
  displayedSteps: [],
  nextStepOverrides: {}
}

export const { reducer: stepsReducer, actions: stepsActions } = createAppSlice({
  name: 'Steps',
  initialState: stepsInitialState,
  reducers: (create) => ({
    setStepsVisibility: create.preparedReducer(
      (shownStepUuids: string[], hiddenStepUuids: string[]) => ({
        payload: { shownStepUuids, hiddenStepUuids }
      }),
      (state, action) => {
        const toShow = new Set(action.payload.shownStepUuids)
        const toHide = new Set(action.payload.hiddenStepUuids)
        const currentlyDisplayedIds = new Set(
          state.displayedSteps.map((s) => s.uuid)
        )

        state.displayedSteps = state.allSteps.filter((s) => {
          if (toShow.has(s.uuid)) return true
          if (toHide.has(s.uuid)) return false
          return currentlyDisplayedIds.has(s.uuid)
        })
      }
    ),

    setCurrentStep: create.preparedReducer(
      (stepUuid: string) => ({ payload: { stepUuid } }),
      (state, action) => {
        state.currentStepUuid = action.payload.stepUuid
      }
    ),

    goToNextStep: create.reducer((state) => {
      const { currentStepUuid, displayedSteps, nextStepOverrides } = state
      if (!currentStepUuid || currentStepUuid === REVIEW_STEP_UUID) return

      const override = nextStepOverrides[currentStepUuid]
      if (override && displayedSteps.some((s) => s.uuid === override)) {
        state.currentStepUuid = override
        return
      }

      const currentStepIndex = displayedSteps.findIndex(
        (step) => step.uuid === currentStepUuid
      )

      if (currentStepIndex === displayedSteps.length - 1) {
        state.currentStepUuid = REVIEW_STEP_UUID
        return
      }

      state.currentStepUuid = displayedSteps[currentStepIndex + 1]?.uuid
    }),

    goToPreviousStep: create.reducer((state) => {
      const { currentStepUuid, displayedSteps } = state

      if (!currentStepUuid) return

      if (currentStepUuid === REVIEW_STEP_UUID) {
        state.currentStepUuid = displayedSteps[displayedSteps.length - 1]?.uuid
        return
      }

      const currentStepIndex = displayedSteps.findIndex(
        (step) => step.uuid === currentStepUuid
      )

      const previousUuid = displayedSteps[currentStepIndex - 1]?.uuid

      if (previousUuid) state.currentStepUuid = previousUuid
    }),

    goToLastStep: create.reducer((state) => {
      state.currentStepUuid = REVIEW_STEP_UUID
    }),

    goToFirstStep: create.reducer((state) => {
      state.currentStepUuid = state.displayedSteps[0]?.uuid
    }),

    setNextStep: create.preparedReducer(
      (currentStepUuid: string, nextStepUuid: string) => ({
        payload: { currentStepUuid, nextStepUuid }
      }),
      (state, action) => {
        const { currentStepUuid, nextStepUuid } = action.payload
        state.nextStepOverrides[currentStepUuid] = nextStepUuid
      }
    ),

    clearNextStepOverride: create.preparedReducer(
      (stepUuid: string) => ({ payload: { stepUuid } }),
      (state, action) => {
        delete state.nextStepOverrides[action.payload.stepUuid]
      }
    )
  }),
  extraReducers: (builder) => {
    builder.addCase(getInteractionModel.pending, () => stepsInitialState)

    builder.addCase(getInteractionModel.fulfilled, (state, action) => {
      const { flatData } = action.payload

      if (!flatData?.steps?.length) return

      state.allSteps = flatData.steps || []
      state.displayedSteps = flatData.steps || []

      const initialStepUuid =
        flatData.initial_step_uuid || flatData.steps[0].uuid

      state.currentStepUuid = initialStepUuid
    })
  }
})

export default stepsReducer
